import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { buildApiUrl, toUploadUrl } from '../config/api';
import { packageAPI, reviewAPI } from '../services/api';
import { buildPackageUrl, slugify } from '../utils/slugs';
import './../styles/package.css';

const PackageDetails = () => {
    const { id, slug } = useParams();
    const navigate = useNavigate();
    const [packageData, setPackageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedDay, setExpandedDay] = useState(null);
    const [activeTab, setActiveTab] = useState('itinerary');
    const [activeImage, setActiveImage] = useState(0);
    const [showAllFaq, setShowAllFaq] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [reviewMeta, setReviewMeta] = useState({ count: 0, average_rating: 0 });
    const [reviewForm, setReviewForm] = useState({ rating: 5, review: '', customer_name: '', customer_email: '' });
    const [reviewSubmitting, setReviewSubmitting] = useState(false);
    const [reviewMessage, setReviewMessage] = useState('');
    
    // Wallet states
    const [walletBalance, setWalletBalance] = useState(0);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const getImageUrl = useCallback((imagePath) => (
        toUploadUrl(imagePath, {
            fallback: 'https://placehold.co/1200x600/1a1a1a/ffffff?text=No+Image'
        })
    ), []);

    // Helper function to get stay type icon
    const getStayTypeIcon = (type) => {
        switch(type?.toLowerCase()) {
            case 'hotel': return 'fa-hotel';
            case 'houseboat': return 'fa-ship';
            case 'resort': return 'fa-tree';
            case 'camp': return 'fa-campground';
            case 'homestay': return 'fa-home';
            default: return 'fa-hotel';
        }
    };

    // Helper function to get stay type label
    const getStayTypeLabel = (type) => {
        return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Hotel';
    };

// ✅ Fetch wallet balance from working API
const fetchWalletBalance = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
        setWalletBalance(0);
        return;
    }
    
    try {
        console.log('💰 Fetching wallet balance...');
        const response = await fetch(buildApiUrl('/wallet/balance'), {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        console.log('Wallet response:', data);
        
        if (data.success) {
            setWalletBalance(parseFloat(data.balance));
        } else {
            setWalletBalance(0);
        }
    } catch (error) {
        console.error('Error fetching wallet:', error);
        setWalletBalance(0);
    }
}, []);

// ✅ Combined auth and wallet check
const checkAuthAndWallet = useCallback(async () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    console.log('🔍 Checking auth...');
    console.log('Token:', token ? '✅ Present' : '❌ Missing');
    console.log('User:', userData ? '✅ Present' : '❌ Missing');
    
    if (token && userData) {
        try {
                const user = JSON.parse(userData);
                setIsLoggedIn(true);
                setReviewForm((prev) => ({
                    ...prev,
                    customer_name: user.name || user.username || '',
                    customer_email: user.email || ''
                }));
                console.log('✅ User logged in:', user.name);
            
            // Fetch wallet balance
            await fetchWalletBalance();
        } catch (error) {
            console.error('Error parsing user:', error);
            setIsLoggedIn(false);
        }
    } else {
        console.log('❌ User not logged in');
        setIsLoggedIn(false);
        setWalletBalance(0);
    }
}, [fetchWalletBalance]);

const fetchReviews = useCallback(async () => {
    try {
        const response = await reviewAPI.getReviews('package', id);
        setReviews(response.data?.data || []);
        setReviewMeta(response.data?.meta || { count: 0, average_rating: 0 });
    } catch (error) {
        console.error('Error fetching package reviews:', error);
    }
}, [id]);
 

  

    const fetchPackageDetails = useCallback(async () => {
        try {
            setLoading(true);
            const response = await packageAPI.getPackageById(id);
            
            console.log('📦 Package data received:', response.data);
            
            // Process image URLs
            const data = {
                ...response.data,
                image: getImageUrl(response.data.image),
                gallery: response.data.gallery?.map(img => getImageUrl(img)) || [],
                itinerary: Array.isArray(response.data.itinerary) ? response.data.itinerary : [],
                stay_plan: Array.isArray(response.data.stay_plan) ? response.data.stay_plan : [],
                inclusions: Array.isArray(response.data.inclusions) ? response.data.inclusions : [],
                exclusions: Array.isArray(response.data.exclusions) ? response.data.exclusions : [],
                highlights: Array.isArray(response.data.highlights) ? response.data.highlights : []
            };
            
            setPackageData(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching package:', err);
            setError('Failed to load package details. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [getImageUrl, id]);

    useEffect(() => {
        fetchPackageDetails();
        checkAuthAndWallet();
        fetchReviews();
        window.scrollTo(0, 0);
    }, [checkAuthAndWallet, fetchPackageDetails, fetchReviews, id]);

    useEffect(() => {
        if (!packageData?.title || !id) return;

        const expectedSlug = slugify(packageData.title);
        if (slug !== expectedSlug) {
            navigate(buildPackageUrl(id, packageData.title), { replace: true });
        }
    }, [id, navigate, packageData, slug]);

    const calculateDiscount = () => {
        if (packageData?.original_price && packageData?.price) {
            const discount = ((packageData.original_price - packageData.price) / packageData.original_price * 100).toFixed(0);
            return discount > 0 ? discount : null;
        }
        return null;
    };

    // Calculate total price with GST
    const calculateTotalPrice = () => {
        if (!packageData?.price) return 0;
        return packageData.price * 1.05;
    };

    // Format itinerary data
    const formattedItinerary = packageData?.itinerary || [];
    const formattedStayPlan = packageData?.stay_plan || [];
    const totalPrice = calculateTotalPrice();
    const discount = calculateDiscount();
    const galleryImages = packageData?.gallery?.length ? packageData.gallery : [packageData?.image].filter(Boolean);
    const activeGalleryImage = galleryImages[activeImage] || packageData?.image;
    const packageTypeLabel = packageData?.type
        ? packageData.type.charAt(0).toUpperCase() + packageData.type.slice(1)
        : 'Premium';
    const packageRegion = packageData?.destinations?.split('-')[0]?.trim() || packageData?.destinations || 'Kashmir';
    const packageOverview = packageData?.long_description || packageData?.description || `This package blends comfortable stays, easy logistics, and a balanced itinerary so you can experience ${packageData?.destinations || 'every highlight'} without feeling rushed.`;
    const liveAverageRating = Number(reviewMeta.average_rating || 0) > 0
        ? Number(reviewMeta.average_rating).toFixed(1)
        : Number(packageData?.rating || 0) > 0
            ? Number(packageData.rating).toFixed(1)
            : '4.5';
    const liveReviewCount = Number(reviewMeta.count || 0) > 0
        ? Number(reviewMeta.count)
        : Number(packageData?.reviews || 0);

    // Dynamic FAQ based on package type and auth status
    const getFAQs = () => {
        const baseFAQs = [
            {
                question: 'What is the best time to visit?',
                answer: `The best time to visit ${packageData?.destinations?.split('-')[0] || 'Kashmir'} is from April to October for pleasant weather. For snow activities, December to February is ideal.`
            },
            {
                question: 'Is this package customizable?',
                answer: 'Yes, we offer full customization options. You can add/remove days, change hotels, modify activities, or add special experiences. Contact our travel experts for a personalized itinerary.'
            },
            {
                question: 'What is the cancellation policy?',
                answer: 'Free cancellation up to 30 days before departure. 50% cancellation fee between 15-30 days. No refund within 15 days. Travel insurance is recommended.'
            }
        ];

        // Add wallet FAQ only if user is logged in and has wallet balance
        if (isLoggedIn) {
            baseFAQs.push({
                question: 'Can I use my wallet balance for this booking?',
                answer: `Yes! You have ₹${walletBalance.toLocaleString()} in your wallet. You can use it at checkout. Simply select "Use Wallet" during booking.`
            });
        }

        if (packageData?.type === 'honeymoon') {
            baseFAQs.push({
                question: 'Do you offer special honeymoon arrangements?',
                answer: 'Yes! We provide complimentary cake, flower decoration, candlelight dinner, and couple spa treatments for honeymoon packages.'
            });
        }

        if (packageData?.type === 'adventure') {
            baseFAQs.push({
                question: 'What adventure activities are included?',
                answer: 'Activities vary by season. Summer offers trekking and rafting, while winter provides skiing and snowboarding. Equipment rental can be arranged.'
            });
        }

        if (packageData?.type === 'family') {
            baseFAQs.push({
                question: 'Is this package suitable for children?',
                answer: 'Yes, this family-friendly package includes activities suitable for children, child-friendly meals, and comfortable accommodations.'
            });
        }

        return baseFAQs;
    };

    const faqs = getFAQs();
    const displayedFaqs = showAllFaq ? faqs : faqs.slice(0, 3);

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        if (!reviewForm.customer_name || !reviewForm.review) {
            setReviewMessage('Please add your name and review.');
            return;
        }

        try {
            setReviewSubmitting(true);
            await reviewAPI.createReview({
                entity_type: 'package',
                entity_id: id,
                customer_name: reviewForm.customer_name,
                customer_email: reviewForm.customer_email,
                rating: reviewForm.rating,
                review: reviewForm.review
            });
            setReviewMessage('Review submitted successfully. It will appear after admin approval.');
            setReviewForm((prev) => ({ ...prev, review: '', rating: 5 }));
        } catch (error) {
            setReviewMessage(error.response?.data?.message || 'Failed to submit review.');
        } finally {
            setReviewSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="package-loading">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading amazing package details...</p>
                </div>
            </div>
        );
    }

    if (error || !packageData) {
        return (
            <div className="package-error">
                <div className="error-content">
                    <i className="fas fa-exclamation-circle"></i>
                    <h3>Package Not Found</h3>
                    <p>{error || 'The package you are looking for does not exist.'}</p>
                    <Link to="/packages" className="error-btn">
                        <i className="fas fa-arrow-left"></i>
                        Browse All Packages
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="package-details-page">
            <SEO
                title={packageData ? `${packageData.title} | RedGoTrip` : 'Package Details | RedGoTrip'}
                description={packageData ? `${packageData.title} in ${packageData.destinations}. Check itinerary, stay plan, inclusions, reviews and booking options on RedGoTrip.` : 'Explore package details, itinerary and booking information on RedGoTrip.'}
                canonicalPath={packageData ? `/package/${packageData.id}/${slugify(packageData.title)}` : `/package/${id}${slug ? `/${slug}` : ''}`}
                keywords={packageData ? `${packageData.title}, ${packageData.destinations}, RedGoTrip package, holiday itinerary` : 'RedGoTrip package details'}
                image={packageData?.image ? getImageUrl(packageData.image) : undefined}
                jsonLd={packageData ? {
                    '@context': 'https://schema.org',
                    '@type': 'Product',
                    name: packageData.title,
                    description: `${packageData.title} package for ${packageData.destinations}`,
                    image: packageData.image ? getImageUrl(packageData.image) : undefined,
                    brand: { '@type': 'Brand', name: 'RedGoTrip' },
                    offers: {
                        '@type': 'Offer',
                        priceCurrency: 'INR',
                        price: String(packageData.price || 0),
                        availability: 'https://schema.org/InStock',
                        url: `https://redgotrips.com/package/${packageData.id}/${slugify(packageData.title)}`
                    }
                } : null}
            />
            <div className="details-container">
                <div className="breadcrumb">
                    <Link to="/">
                        <i className="fas fa-home"></i> Home
                    </Link>
                    <i className="fas fa-chevron-right"></i>
                    <Link to="/packages">Packages</Link>
                    <i className="fas fa-chevron-right"></i>
                    <span>{packageData.title}</span>
                </div>

                <section className="details-hero">
                    <div className="hero-copy">
                        <div className="hero-topline">
                            <span className="hero-badge">
                                <i className="fas fa-suitcase-rolling"></i>
                                {packageTypeLabel} Package
                            </span>
                            {discount && (
                                <span className="hero-offer-pill">
                                    Save {discount}%
                                </span>
                            )}
                        </div>
                        <h1 className="hero-title">{packageData.title}</h1>
                        <div className="hero-meta">
                            <div className="meta-item">
                                <i className="fas fa-map-marker-alt"></i>
                                <span>{packageData.destinations}</span>
                            </div>
                            <div className="meta-item">
                                <i className="fas fa-calendar-alt"></i>
                                <span>{packageData.duration}</span>
                            </div>
                            <div className="meta-item">
                                <i className="fas fa-star"></i>
                                <span>{liveAverageRating} rating{liveReviewCount > 0 ? ` • ${liveReviewCount} review${liveReviewCount === 1 ? '' : 's'}` : ''}</span>
                            </div>
                            <div className="meta-item">
                                <i className="fas fa-users"></i>
                                <span>Perfect for families, couples & groups</span>
                            </div>
                        </div>
                        <p className="hero-description">
                            {packageData.description || `A thoughtfully planned ${packageData.duration || ''} itinerary covering ${packageData.destinations || 'the region'} with stays, transfers and sightseeing arranged for a smoother trip.`}
                        </p>
                        <div className="hero-actions">
                            <Link to={`/booking/${id}`} className="book-now-btn hero-book-btn">
                                <span>Book This Package</span>
                                <i className="fas fa-arrow-right"></i>
                            </Link>
                            <a href="tel:+919335444047" className="secondary-cta">
                                <i className="fas fa-phone-alt"></i>
                                Call Expert
                            </a>
                        </div>
                    </div>

                    <div className="hero-gallery-card">
                        <div className="hero-gallery-main">
                            <img
                                src={activeGalleryImage}
                                alt={packageData.title}
                                className="hero-gallery-image"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://placehold.co/1200x600/1a1a1a/ffffff?text=No+Image';
                                }}
                            />
                            <div className="hero-gallery-caption">
                                <strong>{packageRegion}</strong>
                                <span>{galleryImages.length} travel photos</span>
                            </div>
                        </div>
                        {galleryImages.length > 1 && (
                            <div className="hero-gallery-thumbs">
                                {galleryImages.slice(0, 4).map((img, index) => (
                                    <button
                                        key={index}
                                        className={`hero-thumb ${activeImage === index ? 'active-thumb' : ''}`}
                                        onClick={() => setActiveImage(index)}
                                    >
                                        <img src={img} alt={`Preview ${index + 1}`} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                <div className="quick-info-bar">
                    <div className="info-item">
                        <i className="fas fa-route"></i>
                        <div>
                            <span className="info-label">Route</span>
                            <span className="info-value">{packageData.destinations}</span>
                        </div>
                    </div>
                    <div className="info-item">
                        <i className="fas fa-check-circle"></i>
                        <div>
                            <span className="info-label">Inclusions</span>
                            <span className="info-value">{packageData.inclusions?.length || 0} covered</span>
                        </div>
                    </div>
                    <div className="info-item">
                        <i className="fas fa-map-signs"></i>
                        <div>
                            <span className="info-label">Itinerary</span>
                            <span className="info-value">{formattedItinerary.length || 0} day plans</span>
                        </div>
                    </div>
                    <div className="info-item">
                        <i className="fas fa-hotel"></i>
                        <div>
                            <span className="info-label">Stays</span>
                            <span className="info-value">{formattedStayPlan.length || 0} stay points</span>
                        </div>
                    </div>
                </div>

                <div className="content-grid">
                    <div className="main-content">
                        <div className="card page-intro-card">
                            <div className="intro-copy">
                                <h2>Package Overview</h2>
                                <p>{packageOverview}</p>
                            </div>
                            <div className="intro-pills">
                                <div className="intro-pill">
                                    <i className="fas fa-calendar-day"></i>
                                    <span>{packageData.duration}</span>
                                </div>
                                <div className="intro-pill">
                                    <i className="fas fa-bus"></i>
                                    <span>Transfers as per itinerary</span>
                                </div>
                                <div className="intro-pill">
                                    <i className="fas fa-headset"></i>
                                    <span>Support during trip</span>
                                </div>
                            </div>
                        </div>

                        {packageData.highlights && packageData.highlights.length > 0 && (
                            <div className="highlights-section card">
                                <h2 className="section-title">
                                    <i className="fas fa-star"></i>
                                    Package Highlights
                                </h2>
                                <div className="highlights-grid">
                                    {packageData.highlights.map((highlight, index) => (
                                        <div key={index} className="highlight-card">
                                            <div className="highlight-icon">
                                                <i className="fas fa-check-circle"></i>
                                            </div>
                                            <span>{highlight}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* {galleryImages.length > 0 && (
                            <div className="card">
                                <h2 className="section-title">
                                    <i className="fas fa-camera-retro"></i>
                                    Photos
                                </h2>
                                
                                <div className="featured-image-container">
                                    <div className="featured-image-wrapper">
                                        <img 
                                            src={activeGalleryImage}
                                            alt={`${packageData.title} - View ${activeImage + 1}`}
                                            className="featured-image"
                                            onError={(e) => {
                                                e.target.onerror = null;
e.target.src = 'https://placehold.co/1200x600/1a1a1a/ffffff?text=No+Image';
                                            }}
                                        />
                                        <div className="image-counter-badge">
                                            <i className="fas fa-images"></i>
                                            <span>{activeImage + 1} / {packageData.gallery.length}</span>
                                        </div>
                                        <button 
                                            className="nav-arrow prev-arrow"
                                            onClick={() => setActiveImage(prev => 
                                                prev === 0 ? galleryImages.length - 1 : prev - 1
                                            )}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                        <button 
                                            className="nav-arrow next-arrow"
                                            onClick={() => setActiveImage(prev => 
                                                prev === galleryImages.length - 1 ? 0 : prev + 1
                                            )}
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                </div>

                                <div className="thumbnail-strip">
                                    {galleryImages.map((img, index) => (
                                        <button
                                            key={index}
                                            className={`thumbnail-item ${activeImage === index ? 'active-thumbnail' : ''}`}
                                            onClick={() => setActiveImage(index)}
                                        >
                                            <div className="thumbnail-image-wrapper">
                                                <img 
                                                    src={img} 
                                                    alt={`Thumbnail ${index + 1}`}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://placehold.co/150x100/1a1a1a/ffffff?text=Error';
                                                    }}
                                                />
                                                {activeImage === index && (
                                                    <div className="thumbnail-active-indicator">
                                                        <i className="fas fa-check"></i>
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )} */}

                        <div className="tabs-section card">
                            <div className="tabs-header">
                                <button 
                                    className={`tab-btn ${activeTab === 'itinerary' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('itinerary')}
                                >
                                    <i className="fas fa-map-signals"></i>
                                    Itinerary
                                </button>
                                <button 
                                    className={`tab-btn ${activeTab === 'inclusions' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('inclusions')}
                                >
                                    <i className="fas fa-check-circle"></i>
                                    Inclusions
                                </button>
                                <button 
                                    className={`tab-btn ${activeTab === 'stay' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('stay')}
                                >
                                    <i className="fas fa-hotel"></i> Stay Plan
                                </button>
                                <button 
                                    className={`tab-btn ${activeTab === 'faq' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('faq')}
                                >
                                    <i className="fas fa-question-circle"></i>
                                    FAQ
                                </button>
                            </div>

                            <div className="tab-content">
                                {/* Itinerary Tab */}
                                {activeTab === 'itinerary' && (
                                    <div className="itinerary-tab">
                                        {formattedItinerary.length > 0 ? (
                                            formattedItinerary.map((day, index) => (
                                                <div key={index} className="itinerary-day">
                                                    <div 
                                                        className="day-header"
                                                        onClick={() => setExpandedDay(expandedDay === index ? null : index)}
                                                    >
                                                        <div className="day-badge">
                                                            <span className="day-number">Day {day.day_number}</span>
                                                            {day.meals && (
                                                                <div className="day-meals">
                                                                    {day.meals.split(',').map((meal, idx) => (
                                                                        <span key={idx} className="meal-badge">
                                                                            <i className={`fas fa-${meal.trim().toLowerCase().includes('breakfast') ? 'coffee' : 'utensils'}`}></i>
                                                                            {meal.trim()}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="day-title">
                                                            <h3>{day.title}</h3>
                                                            <span className="expand-icon">
                                                                <i className={`fas fa-chevron-${expandedDay === index ? 'up' : 'down'}`}></i>
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {expandedDay === index && (
                                                        <div className="day-details">
                                                            <p className="day-description">{day.description}</p>
                                                            {day.activities && (
                                                                <div className="day-activities">
                                                                    <h4>Activities:</h4>
                                                                    <div className="activities-list">
                                                                        {day.activities.split(',').map((activity, idx) => (
                                                                            <span key={idx} className="activity-chip">
                                                                                <i className="fas fa-check"></i>
                                                                                {activity.trim()}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="empty-itinerary">
                                                <i className="fas fa-map-signs"></i>
                                                <p>No itinerary details available for this package.</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Inclusions Tab */}
                                {activeTab === 'inclusions' && (
                                    <div className="inclusions-tab">
                                        <div className="inclusions-wrapper">
                                            <div className="inclusion-column">
                                                <h3 className="column-title success">
                                                    <i className="fas fa-check-circle"></i>
                                                    What's Included
                                                </h3>
                                                <ul className="inclusion-list">
                                                    {packageData.inclusions.length > 0 ? (
                                                        packageData.inclusions.map((item, index) => (
                                                            <li key={index} className="inclusion-item success">
                                                                <i className="fas fa-check"></i>
                                                                <span>{item}</span>
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li className="inclusion-item">No inclusions specified</li>
                                                    )}
                                                </ul>
                                            </div>
                                            <div className="inclusion-column">
                                                <h3 className="column-title danger">
                                                    <i className="fas fa-times-circle"></i>
                                                    What's Not Included
                                                </h3>
                                                <ul className="inclusion-list">
                                                    {packageData.exclusions.length > 0 ? (
                                                        packageData.exclusions.map((item, index) => (
                                                            <li key={index} className="inclusion-item danger">
                                                                <i className="fas fa-times"></i>
                                                                <span>{item}</span>
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li className="inclusion-item">No exclusions specified</li>
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Stay Plan Tab */}
                                {activeTab === 'stay' && (
                                    <div className="stay-tab">
                                        {formattedStayPlan.length > 0 ? (
                                            <>
                                                <div className="stay-grid">
                                                    {formattedStayPlan.map((stay, index) => (
                                                        <div key={index} className="stay-card">
                                                            <div className="stay-header">Day {stay.day_number}</div>
                                                            <div className="stay-body">
                                                                <h4>{stay.location}</h4>
                                                                <p className="stay-type">
                                                                    <i className={`fas ${getStayTypeIcon(stay.stay_type)}`}></i>
                                                                    {getStayTypeLabel(stay.stay_type)}
                                                                </p>
                                                                <p className="stay-property">{stay.property_name}</p>
                                                                {stay.room_type && (
                                                                    <p className="stay-room-type">
                                                                        <i className="fas fa-bed"></i> {stay.room_type}
                                                                    </p>
                                                                )}
                                                                {stay.amenities && (
                                                                    <div className="stay-amenities">
                                                                        {stay.amenities.split(',').map((item, idx) => (
                                                                            <span key={idx} className="amenity-tag">
                                                                                {item.trim()}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {stay.description && (
                                                                    <p className="stay-description">{stay.description}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Meal Plan from Itinerary */}
                                                {formattedItinerary.length > 0 && (
                                                    <div className="meal-plan-section">
                                                        <h3 className="meal-plan-title">
                                                            <i className="fas fa-utensils"></i>
                                                            Meal Plan
                                                        </h3>
                                                        <div className="meal-grid">
                                                            {formattedItinerary.map((day, index) => (
                                                                <div key={index} className="meal-card">
                                                                    <span className="meal-day">Day {day.day_number}</span>
                                                                    <div className="meal-items">
                                                                        {day.meals?.split(',').map((meal, idx) => (
                                                                            <span key={idx} className="meal-chip">
                                                                                <i className={`fas fa-${meal.trim().toLowerCase().includes('breakfast') ? 'coffee' : 'utensils'}`}></i>
                                                                                {meal.trim()}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="empty-itinerary">
                                                <i className="fas fa-hotel"></i>
                                                <p>No accommodation details available for this package.</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* FAQ Tab */}
                                {activeTab === 'faq' && (
                                    <div className="faq-tab">
                                        {displayedFaqs.map((faq, index) => (
                                            <div key={index} className="faq-item">
                                                <h4 className="faq-question">
                                                    <i className="fas fa-question-circle"></i>
                                                    {faq.question}
                                                </h4>
                                                <p className="faq-answer">{faq.answer}</p>
                                            </div>
                                        ))}
                                        {faqs.length > 3 && (
                                            <button 
                                                className="view-more-faq"
                                                onClick={() => setShowAllFaq(!showAllFaq)}
                                            >
                                                {showAllFaq ? 'Show Less' : `View All ${faqs.length} FAQs`}
                                                <i className={`fas fa-chevron-${showAllFaq ? 'up' : 'down'}`}></i>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="package-reviews card-surface">
                            <div className="reviews-overview">
                                <div>
                                    <span className="section-kicker">Traveler Reviews</span>
                                    <h3>Guest feedback for this package</h3>
                                    <p>{reviewMeta.count} review{reviewMeta.count === 1 ? '' : 's'} • Average rating {reviewMeta.average_rating || 0}/5</p>
                                </div>
                            </div>
                            {reviews.length > 0 ? (
                                <div className="reviews-list">
                                    {reviews.slice(0, 6).map((review) => (
                                        <div key={review.id} className="review">
                                            <div className="review-header">
                                                <div className="reviewer-info">
                                                    <div className="reviewer-avatar">{review.customer_name?.charAt(0)?.toUpperCase() || 'G'}</div>
                                                    <div>
                                                        <div className="reviewer-name">{review.customer_name}</div>
                                                        <div className="review-stars">
                                                            {[...Array(5)].map((_, index) => (
                                                                <i key={index} className={`fas fa-star ${index < Number(review.rating || 0) ? 'filled' : ''}`}></i>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="review-date">{new Date(review.created_at).toLocaleDateString('en-IN')}</div>
                                            </div>
                                            <p className="review-text">{review.review}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="description-text">No approved reviews yet. Be the first traveler to share your experience.</p>
                            )}

                            <form className="review-form" onSubmit={handleReviewSubmit}>
                                <h4>Write a review</h4>
                                <div className="review-form-grid">
                                    <input
                                        type="text"
                                        placeholder="Your name"
                                        value={reviewForm.customer_name}
                                        onChange={(e) => setReviewForm((prev) => ({ ...prev, customer_name: e.target.value }))}
                                    />
                                    <input
                                        type="email"
                                        placeholder="Your email"
                                        value={reviewForm.customer_email}
                                        onChange={(e) => setReviewForm((prev) => ({ ...prev, customer_email: e.target.value }))}
                                    />
                                    <select
                                        value={reviewForm.rating}
                                        onChange={(e) => setReviewForm((prev) => ({ ...prev, rating: Number(e.target.value) }))}
                                    >
                                        {[5, 4, 3, 2, 1].map((rating) => <option key={rating} value={rating}>{rating} Star</option>)}
                                    </select>
                                </div>
                                <textarea
                                    rows="4"
                                    placeholder="Share your experience"
                                    value={reviewForm.review}
                                    onChange={(e) => setReviewForm((prev) => ({ ...prev, review: e.target.value }))}
                                ></textarea>
                                {reviewMessage && <div className="review-message">{reviewMessage}</div>}
                                <button type="submit" className="book-now-btn review-submit-btn" disabled={reviewSubmitting}>
                                    {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                                </button>
                            </form>
                        </div>
                    </div>

                    <div className="booking-sidebar">
                        <div className="booking-card sticky">
                            <div className="sidebar-badge-row">
                                <span className="sidebar-badge">
                                    <i className="fas fa-bolt"></i>
                                    Instant confirmation support
                                </span>
                            </div>
                            <div className="price-header">
                                <span className="price-label">Starting from</span>

                                <div className="price-main">
                                    <span className="current-price">
                                        ₹{packageData.price?.toLocaleString()}
                                    </span>
                                    {packageData.original_price && packageData.original_price > packageData.price && (
                                        <span className="original-price">
                                            ₹{packageData.original_price.toLocaleString()}
                                        </span>
                                    )}
                                </div>
                                <span className="price-note">per person, excluding optional add-ons</span>
                                {discount && (
                                    <span className="discount-tag-details">
                                        Save {discount}%
                                    </span>
                                )}
                            </div>

                            <div className="booking-summary-strip">
                                <div className="summary-mini-item">
                                    <span className="summary-mini-label">Duration</span>
                                    <strong>{packageData.duration}</strong>
                                </div>
                                <div className="summary-mini-item">
                                    <span className="summary-mini-label">Destination</span>
                                    <strong>{packageRegion}</strong>
                                </div>
                            </div>

                            <div className="booking-details">
                                <div className="detail-row">
                                    <span>Base Price</span>
                                    <span>₹{packageData.price?.toLocaleString()}</span>
                                </div>
                                <div className="detail-row">
                                    <span>GST (5%)</span>
                                    <span>₹{(packageData.price * 0.05).toLocaleString()}</span>
                                </div>
                                <div className="detail-row total">
                                    <span>Total</span>
                                    <span>₹{totalPrice.toLocaleString()}</span>
                                </div>
                            </div>

                            {/* ✅ Wallet Balance Display - Only for logged in users */}
                            {isLoggedIn && (
                                <div className={`wallet-info-sidebar ${walletBalance > 0 ? 'has-balance' : 'no-balance'}`}>
                                    <div className="wallet-icon">
                                        <i className="fas fa-wallet"></i>
                                    </div>
                                    <div className="wallet-details">
                                        <span className="wallet-label">Wallet Balance</span>
                                        <span className="wallet-amount">₹{walletBalance.toLocaleString()}</span>
                                    </div>
                                    {walletBalance > 0 ? (
                                        <div className="wallet-message success">
                                            <i className="fas fa-check-circle"></i>
                                            <span>You can use wallet balance at checkout</span>
                                        </div>
                                    ) : (
                                        <div className="wallet-message info">
                                            <i className="fas fa-info-circle"></i>
                                            <span>Add money to wallet for rewards</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <Link to={`/booking/${id}`} className="book-now-btn">
                                <span>Book Now</span>
                                <i className="fas fa-arrow-right"></i>
                            </Link>

                            <div className="secondary-actions">
                                <a href="tel:+919335444047" className="secondary-link-btn">
                                    <i className="fas fa-phone-alt"></i>
                                    Talk to Expert
                                </a>
                                <a href="mailto:bookings@redgotrips.com" className="secondary-link-btn">
                                    <i className="fas fa-envelope"></i>
                                    Get Quote on Email
                                </a>
                            </div>

                            <div className="trust-badges">
                                <div className="trust-badge">
                                    <i className="fas fa-shield-alt"></i>
                                    <span>Secure Booking</span>
                                </div>
                                <div className="trust-badge">
                                    <i className="fas fa-headset"></i>
                                    <span>24/7 Support</span>
                                </div>
                                <div className="trust-badge">
                                    <i className="fas fa-coins"></i>
                                    <span>Best Price</span>
                                </div>
                            </div>

                            <div className="contact-support">
                                <p>Need help with booking?</p>
                                <a href="tel:+919335444047" className="support-phone">
                                    <i className="fas fa-phone-alt"></i>
                                    +91 9335444047
                                </a>
                                <a href="tel:+917895300727" className="support-phone">
                                    <i className="fas fa-phone-alt"></i>
                                    +91 7895300727
                                </a>
                                <a href="mailto:bookings@redgotrips.com" className="support-email">
                                    <i className="fas fa-envelope"></i>
                                    bookings@redgotrips.com
                                </a>
                            </div>

                            <div className="payment-methods">
                                <p>Secure Payment Options</p>
                                <div className="payment-icons">
                                    <i className="fab fa-cc-visa"></i>
                                    <i className="fab fa-cc-mastercard"></i>
                                    <i className="fab fa-cc-amex"></i>
                                    <i className="fab fa-cc-paypal"></i>
                                    <i className="fas fa-credit-card"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PackageDetails;
